import { i18nTranslationHelper } from '@/i18n'

const t = i18nTranslationHelper.getTPage

// ============================================
// MÓDULO BACKOFFICE — ROTAS ADMINISTRATIVAS AUTENTICADAS
// Perfis administrativos: companyManager, internalCompanyManager (superAdmin),
// systemManager, teamManager. Todos os paths ficam sob /backoffice/*.
//
// OBS.: as rotas de companyManager foram migradas de /admin/* para /backoffice/*.
// Os redirects de compatibilidade dos paths legados /admin/* ficam em ./compat.tsx.
// ============================================

export const BACKOFFICE_ROUTES = {
  // ----- Company Manager (migrado /admin -> /backoffice) -----
  COMPANY_MANAGER_DASHBOARD: {
    value: '/backoffice/dashboard',
    name: 'company_manager_dashboard',
    title: t('companyManager.dashboard.pageTitle')
  },
  COMPANY_MANAGER_CREATE_USER_PROFILE: {
    value: '/backoffice/user',
    name: 'company_manager_create_user_profile',
    title: t('companyManager.createUserProfile.pageTitle')
  },
  COMPANY_MANAGER_EDIT_USER_PROFILE: {
    value: '/backoffice/user/:accountId/profile',
    name: 'company_manager_edit_user_profile',
    title: t('companyManager.editUserProfile.pageTitle'),
    computeValue: (args: { accountId: string | number }) =>
      `/backoffice/user/${args.accountId}/profile`
  },
  COMPANY_MANAGER_EDIT_DEPENDENT_USER_PROFILE: {
    value: '/backoffice/dependent_user/:accountId/profile',
    name: 'company_manager_edit_dependent_user_profile',
    title: t('companyManager.editDependentUserProfile.pageTitle'),
    computeValue: (args: { accountId: string | number }) =>
      `/backoffice/dependent_user/${args.accountId}/profile`
  },
  COMPANY_MANAGER_DEPENDENT_LIST: {
    value: '/backoffice/dependent-user',
    name: 'company_manager_dependent_list',
    title: t('companyManager.dependentList.pageTitle')
  },
  COMPANY_MANAGER_HOLDER_SEARCH: {
    value: '/backoffice/holder-user/search',
    name: 'company_manager_holder_search',
    title: t('companyManager.holderSearch.pageTitle')
  },
  COMPANY_MANAGER_DEPENDENT_REGISTRATION: {
    value: '/backoffice/holder-user/:accountId/dependent',
    name: 'company_manager_dependent_registration',
    title: t('companyManager.dependentRegistration.pageTitle'),
    computeValue: (args: { accountId: string | number }) =>
      `/backoffice/holder-user/${args.accountId}/dependent`
  },
  COMPANY_MANAGER_REQUESTS_CONFIGURATION: {
    value: '/backoffice/requests/configuration',
    name: 'company_manager_requests_configuration',
    title: t('companyManager.requestsConfiguration.pageTitle')
  },
  COMPANY_MANAGER_DEPENDENT_ENROLLMENT: {
    value: '/backoffice/dependent-enrollment',
    name: 'company_manager_dependent_enrollment',
    title: t('companyManager.dependentEnrollment.pageTitle')
  },
  COMPANY_MANAGER_UPGRADE_ENROLLMENT: {
    value: '/backoffice/upgrade-enrollment',
    name: 'company_manager_upgrade_enrollment',
    title: t('companyManager.upgradeEnrollment.pageTitle')
  },
  COMPANY_MANAGER_SCHOLARSHIP_RENEWAL: {
    value: '/backoffice/scholarship-renewal-list',
    name: 'company_manager_scholarship_renewal',
    title: t('companyManager.scholarshipRenewal.pageTitle')
  },
  COMPANY_MANAGER_AUTHORIZATIONS: {
    value: '/backoffice/authorizations',
    name: 'company_manager_authorizations',
    title: t('companyManager.authorizations.pageTitle')
  },
  COMPANY_MANAGER_AUTHORIZATION_DETAIL: {
    value: '/backoffice/authorizations/:authorizationId',
    name: 'company_manager_authorization_detail',
    title: t('companyManager.authorizationDetail.pageTitle'),
    computeValue: ({ authorizationId }: { authorizationId: string | number }) =>
      `/backoffice/authorizations/${authorizationId}`,
    computeTitle: ({ authorizationId }: { authorizationId: string | number }) =>
      `${t('companyManager.authorizationDetail.pageTitle')} #${authorizationId}`
  },
  COMPANY_MANAGER_COLLABORATORS: {
    value: '/backoffice/collaborators',
    name: 'company_manager_collaborators',
    title: t('companyManager.collaborators.pageTitle')
  },
  COMPANY_MANAGER_GROUPS: {
    value: '/backoffice/groups',
    name: 'company_manager_groups',
    title: t('companyManager.groups.pageTitle')
  },

  // ----- Internal Company Manager (superAdmin) -----
  INTERNAL_COMPANY_MANAGER_USER_BATCH_DISABLE: {
    value: '/backoffice/user/batch-disable',
    name: 'internal_company_manager_user_batch_disable',
    title: t('internalCompanyManager.userBatchDisable.pageTitle')
  },
  INTERNAL_COMPANY_MANAGER_INVOICE_DETAIL: {
    value: '/backoffice/invoice/:id',
    name: 'internal_company_manager_invoice_detail',
    title: t('internalCompanyManager.invoiceDetail.pageTitle'),
    computeValue: (args: { id: string | number }) => `/backoffice/invoice/${args.id}`
  },
  INTERNAL_COMPANY_MANAGER_INVOICE: {
    value: '/backoffice/invoice',
    name: 'internal_company_manager_invoice',
    title: t('internalCompanyManager.invoice.pageTitle')
  },
  INTERNAL_COMPANY_MANAGER_COMPANIES: {
    value: '/backoffice/companies',
    name: 'internal_company_manager_companies',
    title: t('internalCompanyManager.companies.pageTitle')
  },
  INTERNAL_COMPANY_MANAGER_COMPANY_CREATE: {
    value: '/backoffice/companies/create',
    name: 'internal_company_manager_company_create',
    title: t('internalCompanyManager.companyCreate.pageTitle')
  },
  INTERNAL_COMPANY_MANAGER_COMPANY_DETAIL: {
    value: '/backoffice/company/:id',
    name: 'internal_company_manager_company_detail',
    title: t('internalCompanyManager.companyDetail.pageTitle'),
    computeValue: (args: { id: string | number }) => `/backoffice/company/${args.id}`
  },
  INTERNAL_COMPANY_MANAGER_PARTNER_DETAIL: {
    value: '/backoffice/partner/:id',
    name: 'internal_company_manager_partner_detail',
    title: t('internalCompanyManager.partnerDetail.pageTitle'),
    computeValue: (args: { id: string | number }) => `/backoffice/partner/${args.id}`
  },
  INTERNAL_COMPANY_MANAGER_FIT_SCORE: {
    value: '/backoffice/fit-score',
    name: 'internal_company_manager_fit_score',
    title: t('internalCompanyManager.fitScore.pageTitle')
  },

  // ----- System Manager -----
  SYSTEM_MANAGER_CONFIGURATIONS: {
    value: '/backoffice/system/configurations',
    name: 'system_manager_configurations',
    title: t('systemManager.configurations.pageTitle')
  }
} as const

// ============================================
// CHAVES DE REDIRECT LEGADO /admin -> /backoffice
// Rotas de company-manager que antes eram servidas sob /admin/*.
// Usadas por compat.tsx para montar rotas de redirect 1:1 que
// preservam params dinâmicos e querystring.
// ============================================

export const COMPANY_MANAGER_LEGACY_KEYS = [
  'COMPANY_MANAGER_DASHBOARD',
  'COMPANY_MANAGER_CREATE_USER_PROFILE',
  'COMPANY_MANAGER_EDIT_USER_PROFILE',
  'COMPANY_MANAGER_EDIT_DEPENDENT_USER_PROFILE',
  'COMPANY_MANAGER_DEPENDENT_LIST',
  'COMPANY_MANAGER_HOLDER_SEARCH',
  'COMPANY_MANAGER_DEPENDENT_REGISTRATION',
  'COMPANY_MANAGER_REQUESTS_CONFIGURATION',
  'COMPANY_MANAGER_DEPENDENT_ENROLLMENT',
  'COMPANY_MANAGER_UPGRADE_ENROLLMENT',
  'COMPANY_MANAGER_AUTHORIZATIONS',
  'COMPANY_MANAGER_AUTHORIZATION_DETAIL',
  'COMPANY_MANAGER_COLLABORATORS',
  'COMPANY_MANAGER_GROUPS'
] as const satisfies ReadonlyArray<keyof typeof BACKOFFICE_ROUTES>
